import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { formatIndianNumber } from "Utilities";
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Download, 
  BookOpen, 
  User, 
  Calendar, 
  Tag, 
  ChevronDown, 
  ChevronUp, 
  ArrowUpDown, 
  ChevronLeft, 
  ChevronRight, 
  TrendingUp, 
  TrendingDown, 
  HelpCircle, 
  CheckCircle2, 
  AlertTriangle, 
  Sparkles, 
  Sliders, 
  Info,
  BookMarked,
  X,
  Globe,
  GraduationCap,
  DollarSign,
  Layers,
  Gift
} from "lucide-react";
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  BarChart, 
  Bar, 
  Cell, 
  PieChart as ReChartsPieChart, 
  Pie, 
  Legend 
} from "recharts";
import { 
  useGetSearchAnalyticsLogsQuery, 
  useGetNoResultSearchAnalyticsLogsQuery,
  useGetDashboardProductsQuery
} from "Services/Admin/DashboardApiSlice";
import { useGetAllCategoriesQuery } from "Services/Admin/CategoryAdminApiSlice";
import { useGetAllSubCategoriesQuery } from "Services/Admin/SubCategoryApiSlice";
import { useGetAdminSubClassesQuery } from "Services/Admin/SubClassApiSlice";
import { useGetAllAuthorsQuery } from "Services/Admin/AuthorApiSlice";
import { useGetAllLanguagesQuery } from "Services/Admin/LanguageApiSlice";
import { useGetAllAcademicsQuery } from "Services/Admin/AcademicApiSlice";
import { useGetAllMerchandisesQuery } from "Services/Admin/MerchandiseApiSlice";
import { formatTimestamp } from "Utilities/DateTimeUtils";
import { useEcomConfig } from "hooks/useEcomConfig";

const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 10px;
    transition: background 0.3s ease;
  }
  .custom-scrollbar:hover::-webkit-scrollbar-thumb {
    background: #E2E8F0;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #CBD5E1;
  }
  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }
  .custom-scrollbar:hover {
    scrollbar-color: #E2E8F0 transparent;
  }
`;

const CustomDropdown = ({ icon, defaultText, options, value, onChange }: any) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<'bottom' | 'top'>('bottom');

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < 250 && spaceAbove > spaceBelow) {
        setPosition('top');
      } else {
        setPosition('bottom');
      }
    }
  }, [isOpen]);

  const selectedDisplay = React.useMemo(() => {
    if (!value) return defaultText;
    const found = options?.find((opt: any) => String(opt.value) === String(value));
    return found ? found.label : defaultText;
  }, [value, options, defaultText]);

  return (
    <div ref={dropdownRef} className="relative w-full h-full z-[100]">
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center bg-gray-50 border ${isOpen ? 'border-blue-400 ring-2 ring-blue-50' : 'border-gray-200'} rounded-xl px-3 py-2 text-sm w-full hover:border-blue-300 transition-colors cursor-pointer min-w-0 h-[38px]`}
        title={selectedDisplay}
      >
        <div className="mr-2 shrink-0">{icon}</div>
        <div className="text-[#1B2559] font-bold text-xs truncate flex-1 min-w-0 pr-6 relative">
          {selectedDisplay}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400">
             <ChevronDown className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>

      {isOpen && (
        <div 
          ref={menuRef}
          className={`absolute left-0 w-max min-w-full max-w-[300px] sm:max-w-[400px] bg-white border border-gray-100 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] rounded-xl z-[999] overflow-hidden animate-in fade-in duration-200 ${
            position === 'top' ? 'bottom-[calc(100%+8px)] origin-bottom slide-in-from-bottom-2' : 'top-[calc(100%+8px)] origin-top slide-in-from-top-2'
          }`}
        >
          <style>{scrollbarStyles}</style>
          <div className="max-h-[250px] overflow-y-auto flex flex-col py-1 custom-scrollbar">
            <div 
              onClick={() => { onChange(''); setIsOpen(false); }}
              className={`px-4 py-2.5 text-xs cursor-pointer transition-colors flex-shrink-0 ${!value ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-600 hover:bg-gray-50 font-semibold'}`}
            >
              {defaultText}
            </div>
            {options.map((opt: any) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <div 
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`px-4 py-2.5 text-xs cursor-pointer transition-colors border-l-2 whitespace-normal break-words flex-shrink-0 leading-relaxed ${
                    isSelected 
                      ? 'bg-blue-50 border-blue-500 text-blue-700 font-bold' 
                      : 'border-transparent text-[#1B2559] font-semibold hover:bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  {opt.label}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Dropdown Constants
const SEARCH_TYPES = [
  { label: "All Search Types", value: "" },
  { label: "Search Bar", value: "SEARCH_BAR" },
  { label: "Author", value: "AUTHOR" },
  { label: "Category", value: "CATEGORY" },
  { label: "Subcategory", value: "SUB_CATEGORY" },
  { label: "Book", value: "BOOK" },
  { label: "Merchandise", value: "MERCHANDISE" },
  { label: "Record", value: "RECORD" },
  { label: "Price", value: "PRICE" },
  { label: "Book Condition", value: "BOOK_CONDITION" },
  { label: "Language", value: "LANGUAGE" },
  { label: "Academics", value: "ACADEMIC" },
  { label: "Product Type", value: "PRODUCT_TYPE" },
  { label: "Multiple Filters", value: "MULTIPLE_FILTERS" }
];
const getFilterLabel = (type: string) => {
  switch (type) {
    case "SEARCH_BAR": return "Search";
    case "CATEGORY": return "Category";
    case "SUB_CATEGORY": return "Sub Category";
    case "BOOK": return "Book";
    case "AUTHOR": return "Author";
    case "LANGUAGE": return "Language";
    case "ACADEMIC": return "Academic";
    case "BOOK_CONDITION": return "Condition";
    case "PRICE": return "Price";
    case "MERCHANDISE": return "Merchandise";
    case "PRODUCT_TYPE": return "Product Type";
    case "RECORD": return "Record";
    case "MULTIPLE_FILTERS": return "Multiple Filters";
    default: return type;
  }
};

const getFilterIcon = (type: string) => {
  const size = "w-3 h-3 text-gray-500";
  switch (type) {
    case "SEARCH_BAR": return <Search className={size} />;
    case "CATEGORY": return <BookOpen className={size} />;
    case "SUB_CATEGORY": return <Tag className={size} />;
    case "BOOK": return <BookOpen className={size} />;
    case "AUTHOR": return <User className={size} />;
    case "LANGUAGE": return <Globe className={size} />;
    case "ACADEMIC": return <GraduationCap className={size} />;
    case "BOOK_CONDITION": return <Layers className={size} />;
    case "PRICE": return <DollarSign className={size} />;
    case "MERCHANDISE": return <Gift className={size} />;
    case "PRODUCT_TYPE": return <Sliders className={size} />;
    case "RECORD": return <BookMarked className={size} />;
    default: return <Tag className={size} />;
  }
};

export const RenderSearchCriteria = ({ searchType, searchData }: { searchType: string; searchData: string }) => {
  if (searchType === "MULTIPLE_FILTERS" || (searchData && searchData.trim().startsWith("{") && searchData.trim().endsWith("}"))) {
    try {
      const parsed = JSON.parse(searchData);
      const filters = parsed.filters || [];
      if (Array.isArray(filters) && filters.length > 0) {
        return (
          <div className="flex flex-wrap gap-1.5 py-1">
            {filters.map((filter: any, idx: number) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-gray-50 text-gray-800 rounded-full text-[10px] font-bold border border-gray-200/50 shadow-sm"
              >
                {getFilterIcon(filter.type)}
                <span className="text-gray-400 font-semibold">{getFilterLabel(filter.type)}:</span>
                <span className="font-extrabold text-[#1B2559]">{filter.value}</span>
              </span>
            ))}
          </div>
        );
      }
    } catch (e) {
      console.error("Failed to parse grouped search data:", e);
    }
  }

  // Backward compatibility fallback for single filter/search
  const displayLabel = searchType === "SEARCH_BAR" ? "GLOBAL SEARCH" : (SEARCH_TYPES.find(t => t.value === searchType)?.label || searchType || "PRODUCT");
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 text-blue-800 rounded-full text-[10px] font-bold border border-blue-100/50 shadow-sm">
      {getFilterIcon(searchType)}
      <span className="text-blue-400 font-semibold">{displayLabel}:</span>
      <span className="font-extrabold text-blue-900">{searchData}</span>
    </span>
  );
};


export const AdminAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const plantCode = process.env.REACT_APP_PLANT || "";

  // Load master data for raw ID/code mapping to user-friendly names
  const { data: categoriesData } = useGetAllCategoriesQuery();
  const { data: subCategoriesData } = useGetAllSubCategoriesQuery();
  const { data: subClassesData } = useGetAdminSubClassesQuery();
  const { showAuthor, showLanguage, showAcademic, showMerchandise } = useEcomConfig();
  const { data: authorsData } = useGetAllAuthorsQuery({ plant: plantCode }, { skip: !showAuthor });
  const { data: languagesData } = useGetAllLanguagesQuery({ plant: plantCode }, { skip: !showLanguage });
  const { data: academicsData } = useGetAllAcademicsQuery({ plant: plantCode }, { skip: !showAcademic });
  const { data: merchandisesData } = useGetAllMerchandisesQuery({ plant: plantCode }, { skip: !showMerchandise });

  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(categoriesData)) {
      categoriesData.forEach(item => {
        map.set(item.categoryCode, item.categoryName);
      });
    }
    return map;
  }, [categoriesData]);

  const subCategoryMap = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(subCategoriesData)) {
      subCategoriesData.forEach(item => {
        map.set(item.subCategoryCode, item.subCategoryName);
      });
    }
    const adminSubClasses = subClassesData?.results;
    if (Array.isArray(adminSubClasses)) {
      adminSubClasses.forEach(item => {
        map.set(item.subClassCode, item.subClassName);
      });
    }
    return map;
  }, [subCategoriesData, subClassesData]);

  const authorMap = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(authorsData)) {
      authorsData.forEach(item => {
        map.set(String(item.id), item.author);
      });
    }
    return map;
  }, [authorsData]);

  const languageMap = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(languagesData)) {
      languagesData.forEach(item => {
        map.set(String(item.id), item.language);
      });
    }
    return map;
  }, [languagesData]);

  const academicMap = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(academicsData)) {
      academicsData.forEach(item => {
        map.set(String(item.id), item.academic);
      });
    }
    return map;
  }, [academicsData]);

  const merchandiseMap = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(merchandisesData)) {
      merchandisesData.forEach(item => {
        map.set(String(item.id), item.merchandise);
      });
    }
    return map;
  }, [merchandisesData]);

  const resolveValueToName = useCallback((type: string, value: string): string => {
    if (!value) return value;
    const parts = value.split(",").map(p => p.trim());
    const resolvedParts = parts.map(part => {
      switch (type) {
        case "CATEGORY":
          return categoryMap.get(part) || part;
        case "SUB_CATEGORY": {
          const cleanPart = part.includes("_") ? part.split("_")[1] : part;
          return subCategoryMap.get(cleanPart) || part;
        }
        case "AUTHOR":
          return authorMap.get(part) || part;
        case "LANGUAGE":
          return languageMap.get(part) || part;
        case "ACADEMIC":
          return academicMap.get(part) || part;
        case "MERCHANDISE":
          return merchandiseMap.get(part) || part;
        case "PRODUCT_TYPE": {
          const upper = part.toUpperCase();
          if (upper === "BOOK") return "Book";
          if (upper === "RECORD") return "Record";
          if (upper === "MERCHANDISE") return "Merchandise";
          return part;
        }
        case "BOOK":
        case "MERCHANDISE":
        case "RECORD":
          return part;
        default:
          return part;
      }
    });
    return resolvedParts.join(", ");
  }, [categoryMap, subCategoryMap, authorMap, languageMap, academicMap, merchandiseMap]);

  const resolveMultipleFiltersString = useCallback((searchData: string): string => {
    if (!searchData) return searchData;
    const parts = searchData.split(" | ");
    const resolvedParts = parts.map(part => {
      const idx = part.indexOf(":");
      if (idx === -1) return part;
      const label = part.substring(0, idx).trim();
      const val = part.substring(idx + 1).trim();
      
      let type = "";
      switch (label) {
        case "Category": type = "CATEGORY"; break;
        case "Sub Category": type = "SUB_CATEGORY"; break;
        case "Book": type = "BOOK"; break;
        case "Author": type = "AUTHOR"; break;
        case "Language": type = "LANGUAGE"; break;
        case "Academic": type = "ACADEMIC"; break;
        case "Condition": type = "BOOK_CONDITION"; break;
        case "Price": type = "PRICE"; break;
        case "Merchandise": type = "MERCHANDISE"; break;
        case "Record": type = "RECORD"; break;
        case "Product Type": type = "PRODUCT_TYPE"; break;
        default: type = label.toUpperCase().replace(/\s+/g, "_");
      }
      
      return `${label}: ${resolveValueToName(type, val)}`;
    });
    return resolvedParts.join(" | ");
  }, [resolveValueToName]);

  const getFiltersList = useCallback((item: any): { type: string, value: string }[] => {
    const rawData = item.searchData || "";
    if (item.searchType === "MULTIPLE_FILTERS" || (rawData.trim().startsWith("{") && rawData.trim().endsWith("}"))) {
      try {
        const parsed = JSON.parse(rawData);
        return parsed.filters || [];
      } catch (e) {
        const parts = rawData.split(" | ");
        const list: { type: string, value: string }[] = [];
        parts.forEach((part: string) => {
          const idx = part.indexOf(":");
          if (idx !== -1) {
            const label = part.substring(0, idx).trim();
            const val = part.substring(idx + 1).trim();
            let type = "";
            switch (label) {
              case "Category": type = "CATEGORY"; break;
              case "Sub Category": type = "SUB_CATEGORY"; break;
              case "Book": type = "BOOK"; break;
              case "Author": type = "AUTHOR"; break;
              case "Language": type = "LANGUAGE"; break;
              case "Academic": type = "ACADEMIC"; break;
              case "Condition": type = "BOOK_CONDITION"; break;
              case "Price": type = "PRICE"; break;
              case "Merchandise": type = "MERCHANDISE"; break;
              case "Record": type = "RECORD"; break;
              case "Product Type": type = "PRODUCT_TYPE"; break;
              default: type = label.toUpperCase().replace(/\s+/g, "_");
            }
            list.push({ type, value: val });
          }
        });
        return list;
      }
    }
    return [];
  }, []);

  const getLogDisplayValues = useCallback((item: any) => {
    let displayQueryTerm = item.searchData || "";
    let displaySearchType = item.searchType === "SEARCH_BAR" ? "GLOBAL SEARCH" : (SEARCH_TYPES.find(t => t.value === item.searchType)?.label || item.searchType || "PRODUCT");

    if (item.searchType === "MULTIPLE_FILTERS" || (item.searchData && item.searchData.trim().startsWith("{") && item.searchData.trim().endsWith("}"))) {
      try {
        const parsed = JSON.parse(item.searchData);
        const filters = parsed.filters || [];
        displayQueryTerm = filters.map((f: any) => `${getFilterLabel(f.type)}: ${resolveValueToName(f.type, f.value)}`).join(" | ");
        displaySearchType = "MULTIPLE FILTERS";
      } catch (e) {
        displayQueryTerm = resolveMultipleFiltersString(item.searchData);
        displaySearchType = "MULTIPLE FILTERS";
      }
    } else {
      if ((item.searchType === "BOOK" || item.searchType === "MERCHANDISE" || item.searchType === "RECORD") && 
          (item.searchData === "BOOK" || item.searchData === "MERCHANDISE" || item.searchData === "RECORD") && 
          item.selectedProductName) {
        displayQueryTerm = item.selectedProductName;
      } else {
        displayQueryTerm = resolveValueToName(item.searchType, item.searchData);
      }
    }
    return { displayQueryTerm, displaySearchType };
  }, [resolveValueToName, resolveMultipleFiltersString]);

  const filterLogsBySearchCriteria = useCallback((list: any[], filterType: string, filterVal: string, globalSearchVal: string) => {
    const typeKey = (filterType || "").trim();
    const dataVal = (filterVal || "").trim().toLowerCase();
    const gSearch = (globalSearchVal || "").trim().toLowerCase();
    
    if (!typeKey && !dataVal && !gSearch) return list;
    
    return list.filter((item: any) => {
      // Step 1: If search type filter is active, verify record matches the search type
      if (typeKey) {
        if (typeKey === "MULTIPLE_FILTERS") {
          const isGrouped = item.searchType === "MULTIPLE_FILTERS" || (item.searchData && item.searchData.trim().startsWith("{") && item.searchData.trim().endsWith("}"));
          if (!isGrouped) return false;
        } else {
          // It's a single filter type. It matches if the log itself has this type,
          // OR if it's a MULTIPLE_FILTERS log containing this type inside its filters array.
          const isGrouped = item.searchType === "MULTIPLE_FILTERS" || (item.searchData && item.searchData.trim().startsWith("{") && item.searchData.trim().endsWith("}"));
          if (isGrouped) {
            try {
              const parsed = JSON.parse(item.searchData);
              const filters = parsed.filters || [];
              const hasType = filters.some((f: any) => f.type === typeKey);
              if (!hasType) return false;
            } catch (e) {
              return false;
            }
          } else {
            if (item.searchType !== typeKey) return false;
          }
        }
      }
      
      // Step 2: If search content (dataVal) is active, verify the record matches the search content
      if (dataVal) {
        const { displayQueryTerm, displaySearchType } = getLogDisplayValues(item);
        const rawSearchData = (item.searchData || "").toLowerCase();
        const rawSearchType = (item.searchType || "").toLowerCase();
        const bookName = (item.selectedProductName || "").toLowerCase();
        const creator = (item.crBy || "").toLowerCase();
        const recordId = String(item.id).toLowerCase();
        
        const match = 
          displayQueryTerm.toLowerCase().includes(dataVal) ||
          displaySearchType.toLowerCase().includes(dataVal) ||
          rawSearchData.includes(dataVal) ||
          rawSearchType.includes(dataVal) ||
          bookName.includes(dataVal) ||
          creator.includes(dataVal) ||
          recordId.includes(dataVal);
          
        if (!match) return false;
      }

      // Step 3: If global search (gSearch) is active, verify the record matches the global search query
      if (gSearch) {
        const { displayQueryTerm, displaySearchType } = getLogDisplayValues(item);
        const rawSearchData = (item.searchData || "").toLowerCase();
        const rawSearchType = (item.searchType || "").toLowerCase();
        const bookName = (item.selectedProductName || "").toLowerCase();
        const creator = (item.crBy || "").toLowerCase();
        const recordId = String(item.id).toLowerCase();
        
        const match = 
          displayQueryTerm.toLowerCase().includes(gSearch) ||
          displaySearchType.toLowerCase().includes(gSearch) ||
          rawSearchData.includes(gSearch) ||
          rawSearchType.includes(gSearch) ||
          bookName.includes(gSearch) ||
          creator.includes(gSearch) ||
          recordId.includes(gSearch);
          
        if (!match) return false;
      }
      
      return true;
    });
  }, [getLogDisplayValues]);

  // Filter UI States
  const [dateRange, setDateRange] = useState("Today");
  const [customFrom, setCustomFrom] = useState("2026-05-01");
  const [customTo, setCustomTo] = useState("2026-05-22");
  const [selectedSearchType, setSelectedSearchType] = useState("");
  const [selectedSearchData, setSelectedSearchData] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);

  // Helper function to format Date object into dd/MM/yyyy
  const formatDateStr = (d: Date) => {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper to calculate api fromDate and toDate based on range selection
  const getDatesForRange = (range: string, fromVal: string, toVal: string) => {
    const today = new Date();
    if (range === "Today") {
      const formatted = formatDateStr(today);
      return { fromDate: formatted, toDate: formatted };
    } else if (range === "Yesterday") {
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      const formatted = formatDateStr(yesterday);
      return { fromDate: formatted, toDate: formatted };
    } else if (range === "Last 7 Days") {
      const from = new Date();
      from.setDate(today.getDate() - 7);
      return { fromDate: formatDateStr(from), toDate: formatDateStr(today) };
    } else if (range === "Last 30 Days") {
      const from = new Date();
      from.setDate(today.getDate() - 30);
      return { fromDate: formatDateStr(from), toDate: formatDateStr(today) };
    } else if (range === "Custom Range") {
      const convert = (dateStr: string) => {
        if (!dateStr) return "";
        const parts = dateStr.split("-");
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
      };
      return { fromDate: convert(fromVal), toDate: convert(toVal) };
    }
    return { fromDate: "", toDate: "" };
  };

  // Calculate initial date boundaries (default Today)
  const initialDates = useMemo(() => {
    const today = new Date();
    return {
      fromDate: formatDateStr(today),
      toDate: formatDateStr(today)
    };
  }, []);

  // Applied Filters State (Passed to RTK Query hooks)
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    searchType: "",
    searchData: "",
    selectedProductId: "",
    fromDate: initialDates.fromDate,
    toDate: initialDates.toDate
  });

  // Fetch Successful Searches
  const { 
    data: successLogsData, 
    isLoading: isSuccessLoading, 
    isFetching: isSuccessFetching,
    error: successError 
  } = useGetSearchAnalyticsLogsQuery({
    plant: plantCode,
    search: "",
    searchType: "",
    searchData: "",
    selectedProductId: appliedFilters.selectedProductId,
    fromDate: appliedFilters.fromDate,
    toDate: appliedFilters.toDate
  });

  // Fetch Unsuccessful Searches
  const { 
    data: unsuccessfulLogsData, 
    isLoading: isUnsuccessfulLoading, 
    isFetching: isUnsuccessfulFetching,
    error: unsuccessfulError 
  } = useGetNoResultSearchAnalyticsLogsQuery({
    plant: plantCode,
    search: "",
    searchType: "",
    searchData: "",
    selectedProductId: appliedFilters.selectedProductId,
    fromDate: appliedFilters.fromDate,
    toDate: appliedFilters.toDate
  });

  const { data: productsData } = useGetDashboardProductsQuery({ plant: plantCode });
  const productsList = useMemo(() => Array.isArray(productsData) ? productsData : (productsData?.results || productsData?.data || []), [productsData]);

  // Combined Loading/Fetching Indicator
  const isLoading = isSuccessLoading || isUnsuccessfulLoading || isSuccessFetching || isUnsuccessfulFetching;

  // Process data from API
  const successLogs = useMemo(() => {
    if (!successLogsData?.results) return [];
    const baseList = successLogsData.results.map((item: any) => ({
      ...item,
      dateTime: item.searchDateTime || item.dateTime,
      searchDateTime: item.searchDateTime || item.dateTime
    }));
    return filterLogsBySearchCriteria(baseList, appliedFilters.searchType, appliedFilters.searchData, appliedFilters.search);
  }, [successLogsData, appliedFilters.searchType, appliedFilters.searchData, appliedFilters.search, filterLogsBySearchCriteria]);

  const unsuccessfulLogs = useMemo(() => {
    if (appliedFilters.selectedProductId) return [];
    if (!unsuccessfulLogsData?.results) return [];
    const baseList = unsuccessfulLogsData.results.map((item: any) => ({
      ...item,
      dateTime: item.searchDateTime || item.dateTime,
      searchDateTime: item.searchDateTime || item.dateTime
    }));
    return filterLogsBySearchCriteria(baseList, appliedFilters.searchType, appliedFilters.searchData, appliedFilters.search);
  }, [unsuccessfulLogsData, appliedFilters.selectedProductId, appliedFilters.searchType, appliedFilters.searchData, appliedFilters.search, filterLogsBySearchCriteria]);

  const table2DemandCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    unsuccessfulLogs.forEach((item: any) => {
      const key = (item.searchData || "").trim().toLowerCase();
      if (key) {
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [unsuccessfulLogs]);

  // Extract Error messages from API responses
  const apiError = useMemo(() => {
    const parseError = (err: any) => {
      if (!err) return null;
      if (err.data && typeof err.data === "object") {
        return err.data.message || JSON.stringify(err.data);
      }
      if (typeof err.message === "string") return err.message;
      return "An unexpected API error occurred.";
    };
    return parseError(successError) || parseError(unsuccessfulError);
  }, [successError, unsuccessfulError]);

  // Local Search Input inside Tables
  const [searchTable1, setSearchTable1] = useState("");
  const [searchTable2, setSearchTable2] = useState("");

  // Pagination states
  const [table1Page, setTable1Page] = useState(1);
  const [table2Page, setTable2Page] = useState(1);
  const [table1ItemsPerPage, setTable1ItemsPerPage] = useState(100);
  const [table2ItemsPerPage, setTable2ItemsPerPage] = useState(100);

  // Sorting Tables
  const [table1SortField, setTable1SortField] = useState<string>("id");
  const [table1SortOrder, setTable1SortOrder] = useState<"asc" | "desc">("desc");

  const [table2SortField, setTable2SortField] = useState<string>("id");
  const [table2SortOrder, setTable2SortOrder] = useState<"asc" | "desc">("desc");

  // Apply Action Trigger
  const handleSearchFilters = () => {
    const dateBoundaries = getDatesForRange(dateRange, customFrom, customTo);
    setAppliedFilters({
      search: globalSearch,
      searchType: selectedSearchType,
      searchData: selectedSearchData,
      selectedProductId: selectedProductId,
      fromDate: dateBoundaries.fromDate,
      toDate: dateBoundaries.toDate
    });
    setTable1Page(1);
    setTable2Page(1);
  };

  // Reset Action Trigger
  const handleResetFilters = () => {
    setGlobalSearch("");
    setSelectedSearchType("");
    setSelectedSearchData("");
    setSelectedProductId("");
    setDateRange("Today");
    setAppliedFilters({
      search: "",
      searchType: "",
      searchData: "",
      selectedProductId: "",
      fromDate: initialDates.fromDate,
      toDate: initialDates.toDate
    });
    setTable1Page(1);
    setTable2Page(1);
    setChartSource("ALL");
    setChartTab("TYPE");
  };

  // CSV Export
  const handleExportCSV = (tableName: string, dataList: any[]) => {
    if (!dataList || dataList.length === 0) {
      return;
    }
    
    const isSuccess = tableName.toLowerCase().includes("successful") && !tableName.toLowerCase().includes("unsuccessful");
    const headers = isSuccess 
      ? ["S.No", "Search Type", "Search Data", "Product", "DateTime"]
      : ["S.No", "Search Type", "Search Data", "DateTime"];

    const rows = dataList.map((item, idx) => {
      let displaySearchType = item.searchType === "SEARCH_BAR" ? "GLOBAL SEARCH" : (SEARCH_TYPES.find(t => t.value === item.searchType)?.label || item.searchType || "PRODUCT");
      let displayQueryTerm = item.searchData || "";

      if (item.searchType === "MULTIPLE_FILTERS" || (item.searchData && item.searchData.trim().startsWith("{") && item.searchData.trim().endsWith("}"))) {
        try {
          const parsed = JSON.parse(item.searchData);
          const filters = parsed.filters || [];
          displayQueryTerm = filters.map((f: any) => `${getFilterLabel(f.type)}: ${resolveValueToName(f.type, f.value)}`).join(" | ");
          displaySearchType = "MULTIPLE FILTERS";
        } catch (e) {
          displayQueryTerm = resolveMultipleFiltersString(item.searchData);
          displaySearchType = "MULTIPLE FILTERS";
        }
      } else {
        if ((item.searchType === "BOOK" || item.searchType === "MERCHANDISE" || item.searchType === "RECORD") && 
            (item.searchData === "BOOK" || item.searchData === "MERCHANDISE" || item.searchData === "RECORD") && 
            item.selectedProductName) {
          displayQueryTerm = item.selectedProductName;
        } else {
          displayQueryTerm = resolveValueToName(item.searchType, item.searchData);
        }
      }

      const productVal = item.selectedProductName || "-";

      return isSuccess ? [
        idx + 1,
        displaySearchType,
        displayQueryTerm,
        productVal,
        item.dateTime ? formatTimestamp(item.dateTime) : ""
      ] : [
        idx + 1,
        displaySearchType,
        displayQueryTerm,
        item.dateTime ? formatTimestamp(item.dateTime) : ""
      ];
    });
    
    const csvContent = [headers.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${tableName.replace(/\s+/g, "_")}_Logs.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sorting Helper
  const sortLogs = (data: any[], field: string, order: "asc" | "desc") => {
    return [...data].sort((a: any, b: any) => {
      let aVal = a[field] ?? "";
      let bVal = b[field] ?? "";

      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
      }
      if (typeof bVal === "string") {
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Compute Table 1 Data (Successful)
  const processedTable1Data = useMemo(() => {
    let result = successLogs;

    if (searchTable1.trim()) {
      const q = searchTable1.toLowerCase();
      result = result.filter((item: any) => {
        const { displayQueryTerm, displaySearchType } = getLogDisplayValues(item);
        return (
          displayQueryTerm.toLowerCase().includes(q) || 
          displaySearchType.toLowerCase().includes(q) ||
          (item.selectedProductName || "").toLowerCase().includes(q) ||
          (item.crBy || "").toLowerCase().includes(q) ||
          String(item.id).includes(q)
        );
      });
    }

    return sortLogs(result, table1SortField, table1SortOrder);
  }, [successLogs, searchTable1, table1SortField, table1SortOrder, getLogDisplayValues]);

  const paginatedTable1Data = useMemo(() => {
    const startIndex = (table1Page - 1) * table1ItemsPerPage;
    return processedTable1Data.slice(startIndex, startIndex + table1ItemsPerPage);
  }, [processedTable1Data, table1Page, table1ItemsPerPage]);

  const table1TotalPages = Math.ceil(processedTable1Data.length / table1ItemsPerPage) || 1;

  // Compute Table 2 Data (Unsuccessful)
  const processedTable2Data = useMemo(() => {
    let result = unsuccessfulLogs;

    if (searchTable2.trim()) {
      const q = searchTable2.toLowerCase();
      result = result.filter((item: any) => {
        const { displayQueryTerm, displaySearchType } = getLogDisplayValues(item);
        return (
          displayQueryTerm.toLowerCase().includes(q) || 
          displaySearchType.toLowerCase().includes(q) ||
          (item.crBy || "").toLowerCase().includes(q) ||
          String(item.id).includes(q)
        );
      });
    }

    return sortLogs(result, table2SortField, table2SortOrder);
  }, [unsuccessfulLogs, searchTable2, table2SortField, table2SortOrder, getLogDisplayValues]);

  const paginatedTable2Data = useMemo(() => {
    const startIndex = (table2Page - 1) * table2ItemsPerPage;
    return processedTable2Data.slice(startIndex, startIndex + table2ItemsPerPage);
  }, [processedTable2Data, table2Page, table2ItemsPerPage]);

  const table2TotalPages = Math.ceil(processedTable2Data.length / table2ItemsPerPage) || 1;

  // Compute Analytics Metrics Dynamically
  const stats = useMemo(() => {
    const totalCount = successLogs.length + unsuccessfulLogs.length;
    const successRatio = totalCount > 0 
      ? `${((successLogs.length / totalCount) * 100).toFixed(1)}%`
      : "0.0%";
    const unsuccessfulRatio = totalCount > 0 
      ? `${((unsuccessfulLogs.length / totalCount) * 100).toFixed(1)}%`
      : "0.0%";

    const activeSearchType = appliedFilters.searchType;
    const activeProductId = appliedFilters.selectedProductId;

    const getContextAwareDisplayTerm = (item: any) => {
      // 1. If product/book filter is active, return the product name
      if (activeProductId && item.selectedProductName) {
        return item.selectedProductName;
      }

      // 2. If a specific search type is active (excluding MULTIPLE_FILTERS)
      if (activeSearchType && activeSearchType !== "MULTIPLE_FILTERS") {
        const isGrouped = item.searchType === "MULTIPLE_FILTERS" || (item.searchData && item.searchData.trim().startsWith("{") && item.searchData.trim().endsWith("}"));
        if (isGrouped) {
          try {
            const parsed = JSON.parse(item.searchData);
            const filters = parsed.filters || [];
            const found = filters.find((f: any) => f.type === activeSearchType);
            if (found) {
              if ((activeSearchType === "BOOK" || activeSearchType === "MERCHANDISE" || activeSearchType === "RECORD") && 
                  (found.value === "BOOK" || found.value === "MERCHANDISE" || found.value === "RECORD") && 
                  item.selectedProductName) {
                return item.selectedProductName;
              }
              return resolveValueToName(activeSearchType, found.value);
            }
          } catch (e) {}
        } else if (item.searchType === activeSearchType) {
          if ((item.searchType === "BOOK" || item.searchType === "MERCHANDISE" || item.searchType === "RECORD") && 
              (item.searchData === "BOOK" || item.searchData === "MERCHANDISE" || item.searchData === "RECORD") && 
              item.selectedProductName) {
            return item.selectedProductName;
          }
          return resolveValueToName(item.searchType, item.searchData);
        }
      }

      // 3. Fallback: when Search Type is "All Search Types" (no activeSearchType)
      if (!activeSearchType) {
        const isGrouped = item.searchType === "MULTIPLE_FILTERS" || (item.searchData && item.searchData.trim().startsWith("{") && item.searchData.trim().endsWith("}"));
        if (isGrouped) {
          const filters = getFiltersList(item);
          if (filters.length > 0) {
            // Find by priority: Author, Book, Merchandise, Record, Subcategory, Category, others
            const authorFilter = filters.find((f: any) => f.type === "AUTHOR");
            if (authorFilter) return resolveValueToName("AUTHOR", authorFilter.value);

            const bookFilter = filters.find((f: any) => f.type === "BOOK");
            if (bookFilter) {
              if (bookFilter.value === "BOOK" && item.selectedProductName) {
                return item.selectedProductName;
              }
              return resolveValueToName("BOOK", bookFilter.value);
            }

            const merchandiseFilter = filters.find((f: any) => f.type === "MERCHANDISE");
            if (merchandiseFilter) {
              if (merchandiseFilter.value === "MERCHANDISE" && item.selectedProductName) {
                return item.selectedProductName;
              }
              return resolveValueToName("MERCHANDISE", merchandiseFilter.value);
            }

            const recordFilter = filters.find((f: any) => f.type === "RECORD");
            if (recordFilter) {
              if (recordFilter.value === "RECORD" && item.selectedProductName) {
                return item.selectedProductName;
              }
              return resolveValueToName("RECORD", recordFilter.value);
            }

            const subCatFilter = filters.find((f: any) => f.type === "SUB_CATEGORY");
            if (subCatFilter) return resolveValueToName("SUB_CATEGORY", subCatFilter.value);

            const catFilter = filters.find((f: any) => f.type === "CATEGORY");
            if (catFilter) return resolveValueToName("CATEGORY", catFilter.value);

            return resolveValueToName(filters[0].type, filters[0].value);
          }
        }
      }

      // 4. Default Fallback: combined display value
      const rawTerm = item.searchData || "";
      if (item.searchType === "MULTIPLE_FILTERS" || (rawTerm.trim().startsWith("{") && rawTerm.trim().endsWith("}"))) {
        try {
          const parsed = JSON.parse(rawTerm);
          const filters = parsed.filters || [];
          return filters.map((f: any) => {
            if ((f.type === "BOOK" || f.type === "MERCHANDISE" || f.type === "RECORD") && 
                (f.value === "BOOK" || f.value === "MERCHANDISE" || f.value === "RECORD") && 
                item.selectedProductName) {
              return `${getFilterLabel(f.type)}: ${item.selectedProductName}`;
            }
            return `${getFilterLabel(f.type)}: ${resolveValueToName(f.type, f.value)}`;
          }).join(" | ");
        } catch (e) {
          return resolveMultipleFiltersString(rawTerm);
        }
      }
      if ((item.searchType === "BOOK" || item.searchType === "MERCHANDISE" || item.searchType === "RECORD") && 
          (rawTerm === "BOOK" || rawTerm === "MERCHANDISE" || rawTerm === "RECORD") && 
          item.selectedProductName) {
        return item.selectedProductName;
      }
      return resolveValueToName(item.searchType, rawTerm);
    };

    // Get Top search term (Success)
    const getTopTerm = (list: any[]) => {
      if (list.length === 0) return "N/A";
      const counts: Record<string, number> = {};
      let maxVal = 0;
      let maxTerm = "N/A";
      list.forEach((item: any) => {
        const term = getContextAwareDisplayTerm(item);
        if (term) {
          counts[term] = (counts[term] || 0) + 1;
          if (counts[term] > maxVal) {
            maxVal = counts[term];
            maxTerm = term;
          }
        }
      });
      return maxTerm;
    };

    return {
      total: totalCount,
      success: successLogs.length,
      unsuccessful: unsuccessfulLogs.length,
      ratio: successRatio,
      unsuccessfulRatio,
      topSuccess: getTopTerm(successLogs),
      topFailed: getTopTerm(unsuccessfulLogs)
    };
  }, [successLogs, unsuccessfulLogs, resolveValueToName, appliedFilters.searchType, appliedFilters.selectedProductId]);

  // Request sort helper for Table 1
  const requestTable1Sort = (field: string) => {
    let order: "asc" | "desc" = "asc";
    if (table1SortField === field && table1SortOrder === "asc") {
      order = "desc";
    }
    setTable1SortField(field);
    setTable1SortOrder(order);
  };

  // Request sort helper for Table 2
  const requestTable2Sort = (field: string) => {
    let order: "asc" | "desc" = "asc";
    if (table2SortField === field && table2SortOrder === "asc") {
      order = "desc";
    }
    setTable2SortField(field);
    setTable2SortOrder(order);
  };

  // Color palette for search visualizations (donut slices)
  const CHART_COLORS = ["#4F46E5", "#06B6D4", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6", "#EF4444", "#3B82F6", "#6B7280"];

  // Search analytics breakdown states
  const [chartSource, setChartSource] = useState<"ALL" | "SUCCESS" | "FAILED">("ALL");
  const [chartTab, setChartTab] = useState<"TYPE" | "CATEGORY" | "SUBCATEGORY" | "AUTHOR" | "PRODUCT">("TYPE");

  // Get base logs array matching the source toggle selection
  const chartBaseData = useMemo(() => {
    if (chartSource === "SUCCESS") return successLogs;
    if (chartSource === "FAILED") return unsuccessfulLogs;
    return [...successLogs, ...unsuccessfulLogs];
  }, [successLogs, unsuccessfulLogs, chartSource]);

  // Aggregate stats dynamically based on source filters and visual categorization tab
  const chartData = useMemo(() => {
    if (!chartBaseData || chartBaseData.length === 0) return [];

    const counts: Record<string, number> = {};
    
    if (chartTab === "TYPE") {
      chartBaseData.forEach((item: any) => {
        let type = item.searchType || "OTHER";
        const matched = SEARCH_TYPES.find(t => t.value === type);
        const name = matched ? matched.label : type;
        if (name === "All Search Types" || !name) {
          counts["Other / Unspecified"] = (counts["Other / Unspecified"] || 0) + 1;
        } else {
          counts[name] = (counts[name] || 0) + 1;
        }
      });
    } else if (chartTab === "PRODUCT") {
      // "Top Books" — aggregate selectedProductName across ALL search types
      // to show most frequently clicked/searched products
      chartBaseData.forEach((item: any) => {
        const productName = (item.selectedProductName || "").trim();
        if (productName && productName !== "-") {
          counts[productName] = (counts[productName] || 0) + 1;
        }
      });
    } else {
      let targetType = "";
      if (chartTab === "CATEGORY") targetType = "CATEGORY";
      else if (chartTab === "SUBCATEGORY") targetType = "SUB_CATEGORY";
      else if (chartTab === "AUTHOR") targetType = "AUTHOR";

      chartBaseData.forEach((item: any) => {
        const itemType = item.searchType || "";
        if (itemType === targetType) {
          const term = (item.searchData || "").trim() || "Unspecified";
          const displayLabel = term !== "Unspecified" ? resolveValueToName(targetType, term) : term;
          counts[displayLabel] = (counts[displayLabel] || 0) + 1;
        }
      });
    }

    const sortedData = Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Group items beyond the top 6 into a fallback "Others" slice
    if (sortedData.length > 7) {
      const topRows = sortedData.slice(0, 6);
      const othersVal = sortedData.slice(6).reduce((acc, curr) => acc + curr.value, 0);
      topRows.push({ name: "Others", value: othersVal });
      return topRows;
    }

    return sortedData;
  }, [chartBaseData, chartTab, resolveValueToName]);

  // Compute total volume for breakdown percentage
  const chartTotalVolume = useMemo(() => {
    return chartData.reduce((acc, curr) => acc + curr.value, 0);
  }, [chartData]);
  const activeFiltersCount = 
    (dateRange !== "Today" ? 1 : 0) +
    (selectedSearchType !== "" ? 1 : 0) +
    (selectedProductId !== "" ? 1 : 0) +
    (selectedSearchData !== "" ? 1 : 0);

  return (
    <div className="bg-[#F4F7FE] min-h-screen p-4 md:p-6 font-sans">
      
      {/* NAVIGATION TABS */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <button 
            onClick={() => navigate("/admin/")}
            className="px-5 py-2 text-xs font-bold rounded-full bg-white text-[#1B2559] hover:bg-gray-50 border border-gray-200 shadow-sm flex items-center gap-1.5 hover:shadow-md transition-all"
          >
            <Sliders className="w-3.5 h-3.5 text-[#1B2559]" /> Overview
          </button>
          <button 
            className="px-5 py-2 text-xs font-bold rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] text-white shadow-md border border-transparent flex items-center gap-1.5 transition-all"
          >
            <TrendingUp className="w-3.5 h-3.5 text-white" /> Search Analytics
          </button>
        </div>

        {/* LOADING INDICATOR PIN */}
        {isLoading && (
          <span className="text-[10px] font-black text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full flex items-center gap-1 animate-pulse">
            <RefreshCw className="w-3 h-3 animate-spin" /> Synchronizing data...
          </span>
        )}
      </div>

      {/* ====================================================
          SECTION 1 — PAGE HEADER
          ==================================================== */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <nav className="text-xs font-bold text-gray-400 mb-1 flex items-center gap-1">
            <span className="hover:text-gray-600 cursor-pointer" onClick={() => navigate("/admin/")}>Admin</span>
            <span>/</span>
            <span className="hover:text-gray-600 cursor-pointer" onClick={() => navigate("/admin/")}>Dashboard</span>
            <span>/</span>
            <span className="text-[#2563EB]">Analytics</span>
          </nav>
          <h1 className="text-2xl font-black text-[#1B2559]">Search Analytics Dashboard</h1>
          <p className="text-sm font-semibold text-gray-400">Customer Search Insights & Demand Logs</p>
        </div>

        <div className="flex items-center gap-2">
          <button 
            onClick={() => handleExportCSV("Search_Analytics_Successful", successLogs)}
            className="flex items-center gap-2 bg-gradient-to-r from-[#2563EB] to-[#6366F1] text-white px-4 py-2 rounded-xl text-sm font-bold shadow-md hover:opacity-95 hover:shadow-lg transition-all"
          >
            <Download className="w-4 h-4" />
            Export Success Logs 
          </button>
        </div>
      </div>

      {/* API ERROR ALERT BANNER */}
      {apiError && (
        <div className="mb-6 bg-rose-50 border-l-4 border-rose-500 rounded-xl p-4 flex gap-3 shadow-sm">
          <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-xs font-black text-rose-800 uppercase">Search Analytics API Error</h4>
            <p className="text-xs text-rose-700 font-semibold mt-0.5">{apiError}</p>
          </div>
        </div>
      )}

      {/* ====================================================
          SECTION 2 — GLOBAL SEARCH
          ==================================================== */}
      <div className="bg-white border border-gray-100 rounded-2xl p-2 shadow-sm mb-4 flex items-center transition-shadow hover:shadow-md">
        <Search className="w-5 h-5 text-gray-400 ml-3 mr-2 shrink-0" />
        <input 
          type="text"
          placeholder="Search queries, authors, products, keywords..."
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchFilters()}
          className="bg-transparent border-none text-sm text-[#1B2559] font-semibold focus:ring-0 focus:outline-none w-full min-w-0 outline-none p-2"
        />
        {globalSearch && (
          <button 
            onClick={() => { setGlobalSearch(""); handleSearchFilters(); }}
            className="p-1 mr-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* ====================================================
          SECTION 3 — ADVANCED FILTERS
          ==================================================== */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm mb-6 flex flex-col relative z-50">
        {/* Collapsible Header */}
        <button 
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          className="flex items-center justify-between p-4 w-full text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-black text-[#1B2559] tracking-normal">Advanced Filters</span>
            {activeFiltersCount > 0 && (
              <span className="bg-blue-100 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded-full ml-1">
                {activeFiltersCount}
              </span>
            )}
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isFiltersExpanded ? "rotate-180" : ""}`} />
        </button>

        {/* Expandable Content */}
        {isFiltersExpanded && (
          <div className="p-4 pt-0 border-t border-gray-50">
            {/* Filters grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {/* 1. Date Range Picker */}
              <div className="flex flex-col gap-1.5 relative z-[71]">
                <label className="text-[11px] font-bold text-gray-400">Date Range</label>
                <div className="h-[38px] w-full">
                  <CustomDropdown
                    icon={<Calendar className="w-4 h-4 text-blue-400" />}
                    defaultText="Today"
                    options={[
                      { label: "Yesterday", value: "Yesterday" },
                      { label: "Last 7 Days", value: "Last 7 Days" },
                      { label: "Last 30 Days", value: "Last 30 Days" },
                      { label: "Custom Range", value: "Custom Range" }
                    ]}
                    value={dateRange === "Today" ? "" : dateRange}
                    onChange={(val: string) => setDateRange(val || "Today")}
                  />
                </div>
              </div>
    
              {/* 2. Search Type Dropdown */}
              <div className="flex flex-col gap-1.5 relative z-[70]">
                <label className="text-[11px] font-bold text-gray-400">Search Type</label>
                <div className="h-[38px] w-full">
                  <CustomDropdown
                    icon={<Tag className="w-4 h-4 text-orange-400" />}
                    defaultText="All Search Types"
                    options={SEARCH_TYPES.filter(t => t.value !== "")}
                    value={selectedSearchType}
                    onChange={setSelectedSearchType}
                  />
                </div>
              </div>

              {/* 3. Product Filter Dropdown */}
              <div className="flex flex-col gap-1.5 relative z-[69]">
                <label className="text-[11px] font-bold text-gray-400">Product</label>
                <div className="h-[38px] w-full">
                  <CustomDropdown
                    icon={<Tag className="w-4 h-4 text-purple-400" />}
                    defaultText="All Products"
                    options={productsList.map((p: any) => ({ label: p.productName, value: p.productId }))}
                    value={selectedProductId}
                    onChange={setSelectedProductId}
                  />
                </div>
              </div>

              {/* 4. Search Data Input */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-400">Search Content</label>
                <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm w-full">
                  <BookOpen className="w-4 h-4 text-emerald-400 mr-2 shrink-0" />
                  <input 
                    type="text"
                    placeholder="Specific book or query..."
                    value={selectedSearchData}
                    onChange={(e) => setSelectedSearchData(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchFilters()}
                    className="bg-transparent border-none text-xs text-[#1B2559] font-bold focus:ring-0 focus:outline-none w-full min-w-0 outline-none p-0"
                  />
                </div>
              </div>
            </div>

            {/* Custom Date Pickers (Shown if Custom Range selected) */}
            {dateRange === "Custom Range" && (
              <div className="flex flex-wrap items-center gap-3 bg-gray-50/50 p-3 rounded-xl border border-dashed border-gray-200 mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-gray-400">From:</span>
                  <input 
                    type="date" 
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="bg-white border border-gray-200 text-[#1B2559] text-xs font-bold rounded-lg px-3 py-1.5 outline-none cursor-pointer"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-gray-400">To:</span>
                  <input 
                    type="date" 
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="bg-white border border-gray-200 text-[#1B2559] text-xs font-bold rounded-lg px-3 py-1.5 outline-none cursor-pointer"
                  />
                </div>
              </div>
            )}

            {/* Filter Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-between border-t border-gray-50 mt-4 pt-4 gap-4">
              
              {/* Active Filter Chips */}
              <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                <span className="text-[11px] font-bold text-[#1B2559] mr-1">Applied Filters:</span>
                
                {dateRange !== "Today" && (
                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 text-gray-600 px-2 py-1 rounded-md text-[10px] font-bold transition-all hover:border-gray-300">
                    <Calendar className="w-3 h-3 text-blue-500" />
                    {dateRange}
                    <button onClick={() => { setDateRange("Today"); setTimeout(handleSearchFilters, 50); }} className="hover:bg-gray-200 rounded p-0.5 ml-1 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                {selectedSearchType !== "" && (
                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 text-gray-600 px-2 py-1 rounded-md text-[10px] font-bold transition-all hover:border-gray-300">
                    <Tag className="w-3 h-3 text-orange-500" />
                    {SEARCH_TYPES.find(t => t.value === selectedSearchType)?.label || selectedSearchType}
                    <button onClick={() => { setSelectedSearchType(""); setTimeout(handleSearchFilters, 50); }} className="hover:bg-gray-200 rounded p-0.5 ml-1 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                {selectedProductId !== "" && (
                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 text-gray-600 px-2 py-1 rounded-md text-[10px] font-bold transition-all hover:border-gray-300">
                    <Tag className="w-3 h-3 text-purple-500" />
                    <span className="truncate max-w-[120px]">
                      {productsList.find((p: any) => p.productId === selectedProductId)?.productName || selectedProductId}
                    </span>
                    <button onClick={() => { setSelectedProductId(""); setTimeout(handleSearchFilters, 50); }} className="hover:bg-gray-200 rounded p-0.5 ml-1 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {selectedSearchData !== "" && (
                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 text-gray-600 px-2 py-1 rounded-md text-[10px] font-bold transition-all hover:border-gray-300">
                    <BookOpen className="w-3 h-3 text-emerald-500" />
                    <span className="truncate max-w-[120px]">{selectedSearchData}</span>
                    <button onClick={() => { setSelectedSearchData(""); setTimeout(handleSearchFilters, 50); }} className="hover:bg-gray-200 rounded p-0.5 ml-1 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {activeFiltersCount > 0 && (
                  <button 
                    onClick={handleResetFilters}
                    className="text-[11px] font-bold text-[#2563EB] hover:text-blue-800 ml-2 transition-colors"
                  >
                    Clear all
                  </button>
                )}
                {activeFiltersCount === 0 && (
                  <span className="text-[11px] text-gray-400 italic">None</span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2.5 shrink-0 sm:ml-auto w-full sm:w-auto justify-end">
                <button 
                  onClick={handleResetFilters}
                  className="flex items-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 px-4 py-2 rounded-xl text-xs font-bold transition shadow-sm"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Reset
                </button>
                <button 
                  onClick={handleSearchFilters}
                  className="flex items-center gap-1.5 bg-[#1B2559] hover:bg-[#11183c] text-white px-5 py-2 rounded-xl text-xs font-bold transition shadow-md"
                >
                  <Search className="w-3.5 h-3.5" />
                  Apply Analytics Search
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ====================================================
          SECTION 4 — SUMMARY ANALYTICS CARDS (Dynamic API Data)
          ==================================================== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
        
        {/* Card 1: Total Queries */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col justify-between min-h-[110px] relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-indigo-50 to-blue-50/20 rounded-full translate-x-8 -translate-y-8 group-hover:scale-110 transition-transform" />
          <div className="flex justify-between items-start z-10">
            <span className="text-[11px] font-bold text-gray-400">Total Searches</span>
            <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500"><Search className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 z-10">
            {isLoading ? (
              <div className="h-6 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <h3 className="text-2xl font-black text-[#1B2559]">{formatIndianNumber(stats.total)}</h3>
            )}
            <p className="text-[9px] text-gray-400 mt-1">Successful & failed logs</p>
          </div>
        </div>

        {/* Card 2: Successful Searches */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col justify-between min-h-[110px] relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-blue-50 to-cyan-50/20 rounded-full translate-x-8 -translate-y-8 group-hover:scale-110 transition-transform" />
          <div className="flex justify-between items-start z-10">
            <span className="text-[11px] font-bold text-gray-400">Successful</span>
            <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center text-blue-500"><BookOpen className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 z-10">
            {isLoading ? (
              <div className="h-6 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <h3 className="text-2xl font-black text-emerald-600">{formatIndianNumber(stats.success)}</h3>
            )}
            <p className="text-[9px] text-emerald-500 mt-1">Items found in catalog</p>
          </div>
        </div>

        {/* Card 3: Top Search Term */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col justify-between min-h-[110px] relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-blue-50 to-cyan-50/20 rounded-full translate-x-8 -translate-y-8 group-hover:scale-110 transition-transform" />
          <div className="flex justify-between items-start z-10">
            <span className="text-[11px] font-bold text-gray-400">Top Successful</span>
            <div className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center text-[#2563EB]">
              <TrendingUp className="w-4 h-4 text-rose-500" />
            </div>
          </div>
          <div className="mt-2 z-10">
            {isLoading ? (
              <div className="h-6 w-24 bg-gray-200 animate-pulse rounded" />
            ) : (
              <h3 className="text-xs font-black text-[#1B2559] truncate max-w-full" title={stats.topSuccess}>{stats.topSuccess}</h3>
            )}
            <p className="text-[9px] font-bold text-gray-400 mt-1 truncate">Most searched keyword</p>
          </div>
        </div>

        {/* Card 4: Unsuccessful Searches */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col justify-between min-h-[110px] relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-blue-50 to-cyan-50/20 rounded-full translate-x-8 -translate-y-8 group-hover:scale-110 transition-transform" />
          <div className="flex justify-between items-start z-10">
            <span className="text-[11px] font-bold text-gray-400">Unsuccessful</span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center text-amber-500"><AlertTriangle className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 z-10">
            {isLoading ? (
              <div className="h-6 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <h3 className="text-2xl font-black text-rose-500">{formatIndianNumber(stats.unsuccessful)}</h3>
            )}
            <p className="text-[9px] text-rose-500 mt-1">No results found</p>
          </div>
        </div>

        {/* Card 5: Top Unsuccessful Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col justify-between min-h-[110px] relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-blue-50 to-cyan-50/20 rounded-full translate-x-8 -translate-y-8 group-hover:scale-110 transition-transform" />
          <div className="flex justify-between items-start z-10">
            <span className="text-[11px] font-bold text-gray-400">Top Unsuccessful</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-500"><User className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 z-10">
            {isLoading ? (
              <div className="h-6 w-24 bg-gray-200 animate-pulse rounded" />
            ) : (
              <h3 className="text-xs font-black text-[#1B2559] truncate max-w-full" title={stats.topFailed}>{stats.topFailed}</h3>
            )}
            <p className="text-[9px] text-gray-400 mt-1">Top unmet book demand</p>
          </div>
        </div>

        {/* Card 6: Success Ratio */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col justify-between min-h-[110px] relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-teal-50 to-emerald-50/20 rounded-full translate-x-8 -translate-y-8 group-hover:scale-110 transition-transform" />
          <div className="flex justify-between items-start z-10">
            <span className="text-[11px] font-bold text-gray-400">Success Ratio</span>
            <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center text-teal-500"><CheckCircle2 className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 z-10">
            {isLoading ? (
              <div className="h-6 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <h3 className="text-2xl font-black text-indigo-600">{stats.ratio}</h3>
            )}
            <p className="text-[9px] text-indigo-500 mt-1">Search availability rate</p>
          </div>
        </div>

        {/* Card 7: Unsuccess Ratio */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col justify-between min-h-[110px] relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-secondary-100 to-secondary-200/20 rounded-full translate-x-8 -translate-y-8 group-hover:scale-110 transition-transform" style={{ backgroundImage: "linear-gradient(to bottom right, #ffe9e9, rgba(255, 211, 210, 0.2))" }} />
          <div className="flex justify-between items-start z-10">
            <span className="text-[11px] font-bold text-gray-400">Unsuccess Ratio</span>
            <div className="w-8 h-8 rounded-xl bg-secondary-100 flex items-center justify-center text-secondary-500" style={{ backgroundColor: "#ffe9e9", color: "#ec4a49" }}><TrendingDown className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 z-10">
            {isLoading ? (
              <div className="h-6 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <h3 className="text-2xl font-black text-secondary-400" style={{ color: "#ec4a49" }}>{stats.unsuccessfulRatio}</h3>
            )}
            <p className="text-[9px] text-secondary-400 mt-1" style={{ color: "#ec4a49" }}>Search unavailability rate</p>
          </div>
        </div>

      </div>

      {/* ====================================================
          SECTION 4 — ANALYTICS TABLES (RTK Query API Response)
          ==================================================== */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        
        {/* TABLE 1 — SUCCESSFUL SEARCH LOGS */}
        <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between min-h-[460px] min-w-0 max-w-full overflow-hidden">
          <div>
            <div className="flex items-start justify-between mb-4 gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-black text-[#1B2559] flex items-center gap-1.5">
                  <BookOpen className="w-4 h-4 text-blue-500 shrink-0" /> Successful Search Logs
                </h3>
                <div className="flex flex-col items-start gap-1 mt-1.5">
                  <span className="text-[10px] font-bold bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded-full whitespace-nowrap">Available Results</span>
                  <span className="text-xs font-semibold text-gray-400">Log entries for searches matching items in catalog.</span>
                </div>
              </div>
              <button 
                onClick={() => handleExportCSV("Successful Search Logs", successLogs)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#2563EB] bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-xl font-bold transition shadow-sm shrink-0"
              >
                <Download className="w-3.5 h-3.5" />
                CSV
              </button>
            </div>

            {/* Table Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input 
                type="text" 
                placeholder="Search list locally by query content, type, creator..."
                value={searchTable1}
                onChange={(e) => { setSearchTable1(e.target.value); setTable1Page(1); }}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-xs bg-gray-50/50 text-gray-700 outline-none focus:border-blue-300 transition"
              />
            </div>

            {/* Scrollable Responsive Table Wrapper */}
            <div className={`overflow-x-auto overflow-y-auto ${!isLoading && processedTable1Data.length === 0 ? "h-[220px]" : "h-[655px]"} custom-table-scrollbar w-full border border-gray-50 rounded-xl`}>
              {!isLoading && processedTable1Data.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6 bg-gray-50/10">
                  <BookMarked className="w-12 h-12 text-gray-300 mb-3" />
                  <h4 className="text-sm font-black text-[#1B2559]">No Successful Searches Found</h4>
                  <p className="text-xs text-gray-400 font-semibold max-w-[280px] mt-1">Try modifying filters or entering different query parameters.</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-[10px] font-bold text-gray-400 sticky top-0 z-10">
                      <th className="px-3 py-3 w-16 select-none">
                        S.No
                      </th>
                      <th className="px-3 py-3 select-none">
                        Search Type
                      </th>
                      <th className="px-4 py-3 select-none min-w-[280px]">
                        Search Query / Term
                      </th>
                      <th className="px-4 py-3 select-none">
                        Product
                      </th>
                      <th className="px-3 py-3 text-center select-none">
                        Timestamp
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-xs text-gray-700">
                    {isLoading ? (
                      // Skeleton loader
                      Array.from({ length: 5 }).map((_, idx) => (
                        <tr key={idx} className="animate-pulse">
                          <td className="px-3 py-3.5"><div className="h-3 bg-gray-200 rounded w-8" /></td>
                          <td className="px-3 py-3.5"><div className="h-3 bg-gray-200 rounded w-16" /></td>
                          <td className="px-4 py-3.5"><div className="h-3 bg-gray-200 rounded w-44" /></td>
                          <td className="px-4 py-3.5"><div className="h-3 bg-gray-200 rounded w-28" /></td>
                          <td className="px-3 py-3.5"><div className="h-3 bg-gray-200 rounded w-28 mx-auto" /></td>
                        </tr>
                      ))
                    ) : (
                      paginatedTable1Data.map((row, i) => {
                        let displayQueryTerm = row.searchData;
                        let displaySearchType = row.searchType === "SEARCH_BAR" ? "GLOBAL SEARCH" : (SEARCH_TYPES.find(t => t.value === row.searchType)?.label || row.searchType || "PRODUCT");
                        let isGrouped = false;

                        if (row.searchType === "MULTIPLE_FILTERS" || (row.searchData && row.searchData.trim().startsWith("{") && row.searchData.trim().endsWith("}"))) {
                          try {
                            const parsed = JSON.parse(row.searchData);
                            const filters = parsed.filters || [];
                            displayQueryTerm = filters.map((f: any) => `${getFilterLabel(f.type)}: ${resolveValueToName(f.type, f.value)}`).join(" | ");
                            displaySearchType = "MULTIPLE FILTERS";
                            isGrouped = true;
                          } catch (e) {
                            displayQueryTerm = row.searchData;
                          }
                        } else {
                          displayQueryTerm = resolveValueToName(row.searchType, row.searchData);
                        }

                        return (
                          <tr key={i} className="hover:bg-blue-50/20 transition-colors">
                            <td className="px-3 py-3 font-bold text-gray-400">#{(table1Page - 1) * table1ItemsPerPage + i + 1}</td>
                            <td className="px-3 py-3">
                              <span className={`px-2 py-0.5 rounded text-[9px] font-black border uppercase whitespace-nowrap bg-blue-50 text-blue-600 border-blue-100`}>
                                {displaySearchType}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-black text-[#1B2559] truncate max-w-[320px] whitespace-nowrap" title={displayQueryTerm}>
                              {displayQueryTerm}
                            </td>
                            <td className="px-4 py-3 font-bold text-gray-500 truncate max-w-[200px]" title={row.selectedProductName}>
                              {row.selectedProductName || "-"}
                            </td>
                            <td className="px-3 py-3 text-center text-[10px] font-bold text-gray-400">
                              {row.dateTime ? formatTimestamp(row.dateTime) : "N/A"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Table 1 Pagination UI */}
          {!isLoading && processedTable1Data.length > 0 && (
            <div className="flex items-center justify-between mt-4 text-xs gap-3 w-full flex-wrap sm:flex-nowrap">
              <div className="hidden sm:block text-gray-400 font-semibold whitespace-nowrap">
                Showing {processedTable1Data.length === 0 ? 0 : (table1Page - 1) * table1ItemsPerPage + 1} to {Math.min(table1Page * table1ItemsPerPage, processedTable1Data.length)} of {processedTable1Data.length} records
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-2 ml-auto w-full sm:w-auto justify-center sm:justify-end">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400 font-semibold">Show</span>
                  <select
                    className="py-1 px-1 border border-gray-200 rounded bg-white hover:bg-gray-50 cursor-pointer outline-none focus:border-blue-400 transition-colors text-xs"
                    value={table1ItemsPerPage}
                    onChange={(e) => {
                      const newItemsPerPage = Number(e.target.value);
                      setTable1ItemsPerPage(newItemsPerPage);
                      setTable1Page(1);
                    }}
                  >
                    {[100, 500, 1000, 1500].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all text-xs"
                    disabled={table1Page === 1 || isLoading}
                    onClick={() => setTable1Page(1)}
                    title="First Page"
                  >
                    &lt;&lt;
                  </button>
                  <button
                    className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all text-xs"
                    disabled={table1Page === 1 || isLoading}
                    onClick={() => setTable1Page(p => Math.max(1, p - 1))}
                    title="Previous Page"
                  >
                    &lt;
                  </button>
                  <div className="flex items-center justify-center w-7 h-7 bg-gray-50 border border-gray-200 rounded font-bold text-blue-600 text-xs shrink-0">
                    {table1Page}
                  </div>
                  <button
                    className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all text-xs"
                    disabled={table1Page === table1TotalPages || table1TotalPages === 0 || isLoading}
                    onClick={() => setTable1Page(p => Math.min(table1TotalPages, p + 1))}
                    title="Next Page"
                  >
                    &gt;
                  </button>
                  <button
                    className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all text-xs"
                    disabled={table1Page === table1TotalPages || table1TotalPages === 0 || isLoading}
                    onClick={() => setTable1Page(table1TotalPages)}
                    title="Last Page"
                  >
                    &gt;&gt;
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* TABLE 2 — UNSUCCESSFUL SEARCH LOGS */}
        <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between min-h-[460px] min-w-0 max-w-full overflow-hidden">
          <div>
            <div className="flex items-start justify-between mb-4 gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-black text-[#1B2559] flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" /> Unsuccessful Search Logs
                </h3>
                <div className="flex flex-col items-start gap-1 mt-1.5">
                  <span className="text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-100 px-2 py-0.5 rounded-full whitespace-nowrap">Zero Results / Out of Stock</span>
                  <span className="text-xs font-semibold text-gray-400">Log entries for searches yielding no matching products.</span>
                </div>
              </div>
              <button 
                onClick={() => handleExportCSV("Unsuccessful Search Logs", unsuccessfulLogs)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#2563EB] bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-xl font-bold transition shadow-sm shrink-0"
              >
                <Download className="w-3.5 h-3.5" />
                CSV
              </button>
            </div>

            {/* Table Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input 
                type="text" 
                placeholder="Search list locally by query content, type, creator..."
                value={searchTable2}
                onChange={(e) => { setSearchTable2(e.target.value); setTable2Page(1); }}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-xs bg-gray-50/50 text-gray-700 outline-none focus:border-blue-300 transition"
              />
            </div>

            {/* Scrollable Responsive Table Wrapper */}
            <div className={`overflow-x-auto overflow-y-auto ${!isLoading && processedTable2Data.length === 0 ? "h-[220px]" : "h-[655px]"} custom-table-scrollbar w-full border border-gray-50 rounded-xl`}>
              {!isLoading && processedTable2Data.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6 bg-gray-50/10">
                  <Search className="w-12 h-12 text-gray-300 mb-3" />
                  <h4 className="text-sm font-black text-[#1B2559]">No Unsuccessful Searches Found</h4>
                  <p className="text-xs text-gray-400 font-semibold max-w-[280px] mt-1">All queries retrieved resulted in successful matching items.</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-[10px] font-bold text-gray-400 sticky top-0 z-10">
                      <th className="px-3 py-3 w-16 select-none">
                        S.No
                      </th>
                      <th className="px-3 py-3 select-none">
                        Search Type
                      </th>
                      <th className="px-4 py-3 select-none min-w-[280px]">
                        Unsuccessful Query / Term
                      </th>
                      <th className="px-3 py-3 text-center select-none">
                        Timestamp
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-xs text-gray-700">
                    {isLoading ? (
                      // Skeleton Loader
                      Array.from({ length: 5 }).map((_, idx) => (
                        <tr key={idx} className="animate-pulse">
                          <td className="px-3 py-3.5"><div className="h-3 bg-gray-200 rounded w-8" /></td>
                          <td className="px-3 py-3.5"><div className="h-3 bg-gray-200 rounded w-16" /></td>
                          <td className="px-4 py-3.5"><div className="h-3 bg-gray-200 rounded w-44" /></td>
                          <td className="px-3 py-3.5"><div className="h-3 bg-gray-200 rounded w-28 mx-auto" /></td>
                        </tr>
                      ))
                    ) : (
                      paginatedTable2Data.map((row, i) => {
                        let displayQueryTerm = row.searchData;
                        let displaySearchType = row.searchType === "SEARCH_BAR" ? "GLOBAL SEARCH" : (SEARCH_TYPES.find(t => t.value === row.searchType)?.label || row.searchType || "AUTHOR");
                        let isGrouped = false;

                        if (row.searchType === "MULTIPLE_FILTERS" || (row.searchData && row.searchData.trim().startsWith("{") && row.searchData.trim().endsWith("}"))) {
                          try {
                            const parsed = JSON.parse(row.searchData);
                            const filters = parsed.filters || [];
                            displayQueryTerm = filters.map((f: any) => `${getFilterLabel(f.type)}: ${resolveValueToName(f.type, f.value)}`).join(" | ");
                            displaySearchType = "MULTIPLE FILTERS";
                            isGrouped = true;
                          } catch (e) {
                            displayQueryTerm = row.searchData;
                          }
                        } else {
                          displayQueryTerm = resolveValueToName(row.searchType, row.searchData);
                        }

                        return (
                          <tr key={i} className="hover:bg-rose-50/10 transition-colors">
                            <td className="px-3 py-3 font-bold text-gray-400">#{(table2Page - 1) * table2ItemsPerPage + i + 1}</td>
                            <td className="px-3 py-3">
                              <span className={`px-2 py-0.5 rounded text-[9px] font-black border uppercase whitespace-nowrap bg-rose-50 text-rose-600 border-rose-100`}>
                                {displaySearchType}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-black text-[#1B2559] truncate max-w-[320px] whitespace-nowrap" title={displayQueryTerm}>
                              {displayQueryTerm}
                            </td>
                            <td className="px-3 py-3 text-center text-[10px] font-bold text-gray-400">
                              {row.dateTime ? formatTimestamp(row.dateTime) : "N/A"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Table 2 Pagination UI */}
          {!isLoading && processedTable2Data.length > 0 && (
            <div className="flex items-center justify-between mt-4 text-xs gap-3 w-full flex-wrap sm:flex-nowrap">
              <div className="hidden sm:block text-gray-400 font-semibold whitespace-nowrap">
                Showing {processedTable2Data.length === 0 ? 0 : (table2Page - 1) * table2ItemsPerPage + 1} to {Math.min(table2Page * table2ItemsPerPage, processedTable2Data.length)} of {processedTable2Data.length} records
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-2 ml-auto w-full sm:w-auto justify-center sm:justify-end">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400 font-semibold">Show</span>
                  <select
                    className="py-1 px-1 border border-gray-200 rounded bg-white hover:bg-gray-50 cursor-pointer outline-none focus:border-[#2563EB] transition-colors text-xs"
                    value={table2ItemsPerPage}
                    onChange={(e) => {
                      const newItemsPerPage = Number(e.target.value);
                      setTable2ItemsPerPage(newItemsPerPage);
                      setTable2Page(1);
                    }}
                  >
                    {[100, 500, 1000, 1500].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all text-xs"
                    disabled={table2Page === 1 || isLoading}
                    onClick={() => setTable2Page(1)}
                    title="First Page"
                  >
                    &lt;&lt;
                  </button>
                  <button
                    className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all text-xs"
                    disabled={table2Page === 1 || isLoading}
                    onClick={() => setTable2Page(p => Math.max(1, p - 1))}
                    title="Previous Page"
                  >
                    &lt;
                  </button>
                  <div className="flex items-center justify-center w-7 h-7 bg-gray-50 border border-gray-200 rounded font-bold text-blue-600 text-xs shrink-0">
                    {table2Page}
                  </div>
                  <button
                    className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all text-xs"
                    disabled={table2Page === table2TotalPages || table2TotalPages === 0 || isLoading}
                    onClick={() => setTable2Page(p => Math.min(table2TotalPages, p + 1))}
                    title="Next Page"
                  >
                    &gt;
                  </button>
                  <button
                    className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all text-xs"
                    disabled={table2Page === table2TotalPages || table2TotalPages === 0 || isLoading}
                    onClick={() => setTable2Page(table2TotalPages)}
                    title="Last Page"
                  >
                    &gt;&gt;
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* ====================================================
          SECTION 4.5 — INTERACTIVE SEARCH BREAKDOWN CHARTS
          ==================================================== */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-gray-50 pb-4 mb-6 gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-500 shrink-0" />
            <div>
              <h3 className="text-base font-black text-[#1B2559]">Search Demands & Categories Visualizations</h3>
              <p className="text-xs font-semibold text-gray-400">Distribution analysis of categories, subcategories, authors and products searched by users.</p>
            </div>
          </div>

          {/* Toggle for Success / Failed / All */}
          <div className="flex flex-row flex-nowrap items-center bg-gray-50 border border-gray-200 rounded-xl p-1 gap-1 w-full sm:w-auto justify-between sm:justify-start self-stretch sm:self-auto">
            <button
              onClick={() => setChartSource("ALL")}
              className={`flex-1 min-w-0 sm:flex-initial text-center rounded-lg text-[10px] sm:text-xs font-bold transition-all px-1.5 sm:px-3.5 py-1.5 whitespace-nowrap ${
                chartSource === "ALL"
                  ? "bg-[#1B2559] text-white shadow-sm"
                  : "bg-transparent border-0 text-gray-500 hover:text-[#1B2559]"
              }`}
            >
              All Searches
            </button>
            <button
              onClick={() => setChartSource("SUCCESS")}
              className={`flex-1 min-w-0 sm:flex-initial text-center rounded-lg text-[10px] sm:text-xs font-bold transition-all px-1.5 sm:px-3.5 py-1.5 whitespace-nowrap ${
                chartSource === "SUCCESS"
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "bg-transparent border-0 text-gray-500 hover:text-emerald-600"
              }`}
            >
              Successful
            </button>
            <button
              onClick={() => setChartSource("FAILED")}
              className={`flex-1 min-w-0 sm:flex-initial text-center rounded-lg text-[10px] sm:text-xs font-bold transition-all px-1.5 sm:px-3.5 py-1.5 whitespace-nowrap ${
                chartSource === "FAILED"
                  ? "bg-rose-500 text-white shadow-sm"
                  : "bg-transparent border-0 text-gray-500 hover:text-rose-500"
              }`}
            >
              Unsuccessful
            </button>
          </div>
        </div>

        {/* Tab Controls for Breakdown Category */}
        <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2 overflow-x-auto custom-table-scrollbar whitespace-nowrap">
          {[
            { id: "TYPE", label: "Search Types", icon: Tag },
            { id: "CATEGORY", label: "Top Categories", icon: BookOpen },
            { id: "SUBCATEGORY", label: "Top Subcategories", icon: Sliders },
            { id: "AUTHOR", label: "Top Authors", icon: User },
            { id: "PRODUCT", label: "Top Books", icon: BookMarked },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = chartTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setChartTab(tab.id as any)}
                className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-bold text-xs transition-all ${
                  isActive
                    ? "border-blue-600 text-blue-600 font-extrabold"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? "text-blue-600" : "text-gray-400"}`} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content Section */}
        {isLoading ? (
          <div className="h-[280px] flex flex-col items-center justify-center gap-3">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-xs text-gray-400 font-bold">Recalculating visualization data...</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[280px] flex flex-col items-center justify-center text-center p-6 bg-gray-50/30 rounded-2xl border border-dashed border-gray-200">
            <Info className="w-10 h-10 text-gray-300 mb-2" />
            <h4 className="text-sm font-black text-[#1B2559]">No Visualization Data</h4>
            <p className="text-xs text-gray-400 font-semibold max-w-[340px] mt-1">
              There are no {chartSource === "SUCCESS" ? "successful" : chartSource === "FAILED" ? "unsuccessful" : ""} search logs matching the active filter settings.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 items-center">
            {/* Chart Column */}
            <div className="flex flex-col items-center justify-center">
              <div className="h-64 w-full relative">
                {/* Center label inside Donut */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none mt-[-20px]">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Searches</span>
                  <span className="text-xl font-black text-[#1B2559]">{formatIndianNumber(chartTotalVolume)}</span>
                </div>

                <ResponsiveContainer width="100%" height="100%">
                  <ReChartsPieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.name === "Others" ? "#94A3B8" : CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: any, name: string) => [`${value} searches`, name]}
                      contentStyle={{
                        background: '#FFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: '#1B2559',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                  </ReChartsPieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* List breakdown Column */}
            <div className="flex flex-col gap-4 max-h-[280px] overflow-y-auto pr-2 custom-table-scrollbar">
              <h4 className="text-xs font-black text-[#1B2559] uppercase tracking-wider border-b border-gray-100 pb-2">
                Detailed Breakdown Share
              </h4>
              {chartData.map((item, index) => {
                const color = item.name === "Others" ? "#94A3B8" : CHART_COLORS[index % CHART_COLORS.length];
                const pct = chartTotalVolume > 0 ? ((item.value / chartTotalVolume) * 100).toFixed(1) : "0.0";
                
                return (
                  <div key={item.name} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between text-xs font-bold text-[#1B2559]">
                      <div className="flex items-center gap-2 truncate">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        <span className="truncate text-left" title={item.name}>{item.name}</span>
                      </div>
                      <div className="flex items-center gap-3 font-black">
                        <span>{pct}%</span>
                        <span className="text-[10px] text-gray-400 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                          {item.value} searches
                        </span>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ====================================================
          SECTION 5 — FUTURE ANALYTICS AREA (Placeholder charts/Skeletons)
          ==================================================== */}
      {/* 
      <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm mb-6">
        <div className="flex items-center justify-between border-b border-gray-50 pb-3 mb-6">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-500 animate-pulse" />
            <div>
              <h3 className="text-base font-black text-[#1B2559]">Future Analytics Widgets</h3>
              <p className="text-xs font-semibold text-gray-400">High-fidelity visualization models and forecasting algorithms (Simulated data previews)</p>
            </div>
          </div>
          <span className="text-[10px] font-extrabold uppercase bg-indigo-50 border border-indigo-100 text-indigo-600 px-3 py-1 rounded-full flex items-center gap-1">
            <span>✨</span> Next-Gen Analytics Ready
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

          <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100 flex flex-col justify-between min-h-[300px]">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-black text-[#1B2559] uppercase tracking-wider">Search Trend Graph</h4>
                <span className="text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-100 px-2 py-0.5 rounded font-extrabold">LIVE</span>
              </div>
              <p className="text-[11px] text-gray-400 font-semibold mb-4">Tracking available vs unavailable book search volume over past 7 days</p>
            </div>
            
            <div className="h-44 w-full text-xs">
              {isEmptyState ? (
                <div className="h-full flex items-center justify-center text-gray-400">No data available</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={MOCK_SEARCH_TREND_DATA} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
                    <defs>
                      <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorUnavail" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="date" stroke="#94A3B8" fontSize={9} tickLine={false} />
                    <YAxis stroke="#94A3B8" fontSize={9} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '10px' }} />
                    <Area type="monotone" dataKey="total" name="Total Searches" stroke="#6366F1" strokeWidth={2} fillOpacity={1} fill="url(#colorTotal)" />
                    <Area type="monotone" dataKey="unavailable" name="Unavailable Search" stroke="#F59E0B" strokeWidth={2} fillOpacity={1} fill="url(#colorUnavail)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100 flex flex-col justify-between min-h-[300px]">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-black text-[#1B2559] uppercase tracking-wider">Demand Forecast Model</h4>
                <span className="text-[10px] bg-purple-50 text-purple-600 border border-purple-100 px-2 py-0.5 rounded font-extrabold">PREDICTIVE</span>
              </div>
              <p className="text-[11px] text-gray-400 font-semibold mb-4">AI projection of search volumes for next 5 months based on seasonal trends</p>
            </div>

            <div className="h-44 w-full text-xs">
              {isEmptyState ? (
                <div className="h-full flex items-center justify-center text-gray-400">No data available</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={MOCK_DEMAND_FORECAST_DATA} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={9} tickLine={false} />
                    <YAxis stroke="#94A3B8" fontSize={9} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '10px' }} />
                    <Legend wrapperStyle={{ fontSize: '9px', paddingTop: '10px' }} />
                    <Bar dataKey="forecast" name="Forecast Demand" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100 flex flex-col justify-between min-h-[300px]">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-black text-[#1B2559] uppercase tracking-wider">Category Analytics</h4>
                <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded font-extrabold">RATIO</span>
              </div>
              <p className="text-[11px] text-gray-400 font-semibold mb-4">Breakdown of customer queries by parent book categories</p>
            </div>

            <div className="h-44 w-full text-xs flex items-center justify-center">
              {isEmptyState ? (
                <span className="text-gray-400">No data available</span>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ReChartsPieChart>
                    <Pie
                      data={MOCK_CATEGORY_BREAKDOWN}
                      cx="50%"
                      cy="45%"
                      innerRadius={45}
                      outerRadius={65}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {MOCK_CATEGORY_BREAKDOWN.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '10px' }} />
                    <Legend 
                      layout="horizontal" 
                      verticalAlign="bottom" 
                      align="center"
                      iconSize={6}
                      wrapperStyle={{ fontSize: '8px', paddingTop: '5px', color: '#1B2559' }}
                    />
                  </ReChartsPieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100 flex flex-col justify-between min-h-[300px]">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-black text-[#1B2559] uppercase tracking-wider">Author Popularity</h4>
                <span className="text-[10px] bg-rose-50 text-rose-600 border border-rose-100 px-2 py-0.5 rounded font-extrabold">DISTRIBUTION</span>
              </div>
              <p className="text-[11px] text-gray-400 font-semibold mb-4">Total monthly searches for books filtered by requested authors</p>
            </div>

            <div className="h-44 w-full text-xs">
              {isEmptyState ? (
                <div className="h-full flex items-center justify-center text-gray-400">No data available</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={MOCK_AUTHOR_POPULARITY} layout="vertical" margin={{ top: 5, right: 5, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                    <XAxis type="number" stroke="#94A3B8" fontSize={8} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={8} width={65} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: '10px' }} />
                    <Bar dataKey="searches" fill="#EC4899" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100 flex flex-col justify-between min-h-[300px]">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-black text-[#1B2559] uppercase tracking-wider">Search Conversion Funnel</h4>
                <span className="text-[10px] bg-teal-50 text-teal-600 border border-teal-100 px-2 py-0.5 rounded font-extrabold">FUNNEL</span>
              </div>
              <p className="text-[11px] text-gray-400 font-semibold mb-4">Customer path from initial book search query to final checkout conversion</p>
            </div>

            <div className="flex flex-col gap-2.5 justify-center py-2 flex-1">
              
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-gray-400 w-16 text-right">Searches</span>
                <div className="flex-1 bg-indigo-500 rounded-lg p-2 text-white font-black text-center text-[10px] flex justify-between px-4 shadow-sm">
                  <span>Initial Queries</span>
                  <span>100% (12,845)</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-gray-400 w-16 text-right">Click-Through</span>
                <div className="flex-1 bg-indigo-400 rounded-lg p-2 text-white font-black text-center text-[10px] flex justify-between px-4 shadow-sm w-[80%] mx-auto" style={{ width: '85%' }}>
                  <span>View Details</span>
                  <span>74.5% (9,570)</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-gray-400 w-16 text-right">Add to Cart</span>
                <div className="flex-1 bg-[#3B82F6] rounded-lg p-2 text-white font-black text-center text-[10px] flex justify-between px-4 shadow-sm" style={{ width: '50%' }}>
                  <span>Shopping Cart</span>
                  <span>41.2% (5,292)</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-gray-400 w-16 text-right">Purchase</span>
                <div className="flex-1 bg-emerald-500 rounded-lg p-2 text-white font-black text-center text-[10px] flex justify-between px-4 shadow-sm" style={{ width: '22%' }}>
                  <span>Paid Order</span>
                  <span>18.4% (2,363)</span>
                </div>
              </div>

            </div>
          </div>

          <div className="bg-[#1B2559] rounded-xl p-4 text-white flex flex-col justify-between min-h-[300px] shadow-lg relative overflow-hidden group">
            <div className="absolute right-0 bottom-0 w-32 h-32 bg-gradient-to-br from-indigo-500/20 to-purple-500/10 rounded-full translate-x-12 translate-y-12" />
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-black uppercase tracking-wider text-indigo-200 flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5 text-yellow-400 animate-spin" />
                  AI Catalog Insights
                </h4>
                <span className="text-[9px] bg-yellow-400 text-black px-2 py-0.5 rounded-full font-black">ACTIVE</span>
              </div>
              <p className="text-[11px] text-gray-300 font-semibold mb-4">Generated from unavailable customer search demand & high matching categories</p>
            </div>

            <div className="flex flex-col gap-3 py-2 z-10">
              
              <div className="bg-white/10 rounded-lg p-2.5 border border-white/5 flex gap-2.5 items-start">
                <span className="text-base shrink-0">💡</span>
                <div>
                  <p className="text-[11px] font-black text-white">Stock "The Anarchy" by W. Dalrymple</p>
                  <p className="text-[9px] text-gray-300">Searched 412 times this month. Category: History. Captures ₹1.2L potential revenue.</p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-2.5 border border-white/5 flex gap-2.5 items-start">
                <span className="text-base shrink-0">📈</span>
                <div>
                  <p className="text-[11px] font-black text-white">Category Expansion: Technology</p>
                  <p className="text-[9px] text-gray-300">Designing Data-Intensive Applications demand is high. Expanding Dev books is recommended.</p>
                </div>
              </div>

            </div>

            <div className="text-[9px] text-indigo-200/60 font-medium text-center z-10">
              Last updated today at 2:50 PM • Refresh catalog logic
            </div>
          </div>

        </div>
      </div>
      */}

    </div>
  );
};

export default AdminAnalyticsDashboard;
